import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';
import { useData } from '@hooks/useData';
import { useTranslation } from 'react-i18next';

const ENDPOINT_URL = '/users/toggle-share-location';
const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export type LocationStatus =
    paths[url]['post']['requestBody']['content']['application/json'];
export type LocationStatusSuccessResponse =
    paths[url]['post']['responses']['200']['content']['application/json'];

export interface UpdateLocationToggleSuccessResponse {
    message: string;
}

export const useToggleShareLocation = () => {
    const { t } = useTranslation();
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const { user, setUser } = useData();

    const request = async (props: LocationStatus) => {
        setLoading(true);
        setError(null);

        try {
            const canSendRequest =
                props.share_location === false ||
                (props.share_location === true &&
                    props.latitude != null &&
                    props.longitude != null);

            if (!canSendRequest) {
                const errorMessage = t('availability-toggle.missing-coordenates');
                setError(errorMessage);
                setLoading(false);
                throw new Error(errorMessage);
            }

            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'POST',
                headers: getHeaders(),
                body: JSON.stringify({ ...props }),
            });

            const data: UpdateLocationToggleSuccessResponse = await response.json();

            if (!response.ok) {
                const errorMessage = data.message || t('common.error-occurred');
                const error = new Error(errorMessage) as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            setSuccess(data.message);

            if (user) {
                setUser({
                    ...user,
                    data: {
                        ...user.data,
                        share_location: props.share_location,
                    },
                });
            }
        } catch (err: any) {
            handleCommonErrors(err);
            if (err.message) setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime, success };
};
